import { FC, useMemo } from 'react';
import { useTenantID } from '@/hooks/useTenantID';
import Button from '../core/Button';
import { FormattedMessage } from 'react-intl';
import { adminTypeColor } from '@/types/administrator';
import { useAdministratorTranslations } from '@/hooks/useAdministratorTranslations';
import { cn } from '@/helpers/classnames';
import { ExtendedUserDTO } from '@/hooks/useGetAllAdmins';
import { CurrentUserBadge } from '@/components/CurrentUserBadge';

interface Props {
  administrator: ExtendedUserDTO;
  departments?: ExtendedUserDTO['departments'];
  className?: string;
  currentUserEmail?: string;
}

export const AdministratorListItem: FC<Props> = ({
  administrator,
  departments,
  className,
  currentUserEmail,
}) => {
  const tenantId = useTenantID();
  const getAdminTypeTranslation = useAdministratorTranslations();
  const { id, firstname, lastname, email, adminType } = administrator;
  const isCurrentUser = currentUserEmail && email === currentUserEmail;

  const { readCount, bearerCount, writeCount } = useMemo(() => {
    const counts = { readCount: 0, bearerCount: 0, writeCount: 0 };

    departments?.forEach((department) => {
      if (department.access_level === 'editor') {
        counts.writeCount++;
      } else if (department.access_level === 'bearer-manager') {
        counts.bearerCount++;
      } else {
        counts.readCount++;
      }
    });

    return counts;
  }, [departments]);

  return (
    <Button
      to="/$tenantId/administrators/$administratorId"
      params={{ tenantId, administratorId: id }}
      className={cn('h-fit w-full justify-between font-normal', className)}
    >
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="truncate pr-1 font-medium">
              {firstname ?? ''} {lastname ?? ''}
              {!firstname && !lastname && (
                <i>
                  <FormattedMessage defaultMessage="Anonym administratör" />
                </i>
              )}
            </span>
            {isCurrentUser && <CurrentUserBadge />}
          </div>
          <span
            className={cn(
              'rounded-full px-2 text-sm',
              adminTypeColor[adminType],
            )}
          >
            {getAdminTypeTranslation(adminType)}
          </span>
        </div>
        <div className="flex w-full justify-between gap-1">
          <p className="w-full truncate text-left text-text-700">{email}</p>
          {departments && adminType === 'deptUser' && (
            <>
              {readCount > 0 && (
                <span
                  className={cn(
                    'm-auto whitespace-nowrap rounded-full px-2 text-sm',
                    adminTypeColor.deptReader,
                  )}
                >
                  <FormattedMessage
                    defaultMessage="{count} Läs"
                    values={{ count: readCount }}
                  />
                </span>
              )}
              {bearerCount > 0 && (
                <span
                  className={cn(
                    'm-auto whitespace-nowrap rounded-full px-2 text-sm',
                    adminTypeColor.deptBearerManager,
                  )}
                >
                  <FormattedMessage
                    defaultMessage="{count} Bärarhantering"
                    values={{ count: bearerCount }}
                  />
                </span>
              )}
              {writeCount > 0 && (
                <span
                  className={cn(
                    'm-auto whitespace-nowrap rounded-full px-2 text-sm',
                    adminTypeColor.deptAdmin,
                  )}
                >
                  <FormattedMessage
                    defaultMessage="{count} Skriv"
                    values={{ count: writeCount }}
                  />
                </span>
              )}
            </>
          )}
        </div>
      </div>
    </Button>
  );
};
